import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
  upsertOrGroup,
} from "@/shared/contracts/query-helpers";
import { config } from "@/shared/lib/config";
import {
  fetchLookupForInfinite,
  hydrateLookupItem,
  hydrateLookupItems,
} from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { InfiniteMultiCombobox } from "@/shared/ui/InfiniteMultiCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";

type StudentGroupLookupItem = {
  id: string;
  name: string;
};

type ClassTemplateLookupItem = {
  id: string;
  name: string;
  classTypeName: string;
  levelName: string;
};

function classTemplateIdsFromFilter(
  filter: FilterConditionDTO | undefined,
): string[] {
  if (!filter) return [];
  if (Array.isArray(filter.value)) {
    return filter.value.filter(
      (id): id is string => typeof id === "string" && !!id,
    );
  }
  if (typeof filter.value === "string" && filter.value) return [filter.value];
  return [];
}

export function StudentsFilter() {
  const nav = useNavigate({ from: "/app/students" });
  const search = useSearch({
    from: "/app/students",
  }) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const statusFilter = findCondition(filters, "status");
  const groupIdFilter = findCondition(filters, "groupId");
  const classTemplateIdFilter = findCondition(filters, "classTemplateId");

  const orGroupFilter = filters.find((f) => {
    if (f.op !== "or" || !Array.isArray(f.value)) return false;
    const conditions = f.value as FilterConditionDTO[];
    return conditions.some(
      (c) =>
        c.field === "memberId" ||
        c.field === "name" ||
        c.field === "nickname" ||
        c.field === "phoneNumber",
    );
  }) as FilterConditionDTO | undefined;
  const searchValue =
    (orGroupFilter &&
      Array.isArray(orGroupFilter.value) &&
      ((orGroupFilter.value as FilterConditionDTO[])[0]?.value as string)) ||
    "";

  const [searchInputValue, setSearchInputValue] = useState<string>(searchValue);

  useEffect(() => {
    setSearchInputValue(searchValue);
  }, [searchValue]);

  const debouncedSearch = useDebounceCallback((val: string) => {
    setSearchInputValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = removeConditions(filters, "memberId");
      nextFilters = removeConditions(nextFilters, "name");
      nextFilters = removeConditions(nextFilters, "nickname");
      nextFilters = removeConditions(nextFilters, "phoneNumber");
      nextFilters = upsertOrGroup(nextFilters, [
        { field: "memberId", op: "contains", value: val },
        { field: "name", op: "contains", value: val },
        { field: "nickname", op: "contains", value: val },
        { field: "phoneNumber", op: "contains", value: val },
      ]);
    } else {
      nextFilters = removeConditions(filters, "memberId");
      nextFilters = removeConditions(nextFilters, "name");
      nextFilters = removeConditions(nextFilters, "nickname");
      nextFilters = removeConditions(nextFilters, "phoneNumber");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const statusValue =
    typeof statusFilter?.value === "string" ? statusFilter.value : "";
  const groupIdValue =
    typeof groupIdFilter?.value === "string" ? groupIdFilter.value : "";
  const classTemplateIds = classTemplateIdsFromFilter(classTemplateIdFilter);
  const hasFilter =
    !!searchValue ||
    !!statusValue ||
    !!groupIdValue ||
    classTemplateIds.length > 0;

  const classTemplatesLookupUrl = `${config.apiUrl}/classes/templates/lookup`;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາ Member ID, ຊື່, ຫຼືເບີໂທ..."
            defaultValue={searchInputValue}
            onChange={(e) => debouncedSearch(e.target.value)}
            className="h-8 sm:max-w-xs"
          />

          <InfiniteCombobox<StudentGroupLookupItem>
            queryKey={["master", "student-groups", "lookup"]}
            queryFn={(args) =>
              fetchLookupForInfinite<StudentGroupLookupItem>(
                `${config.apiUrl}/master/student-groups/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem<StudentGroupLookupItem>(
                `${config.apiUrl}/master/student-groups/lookup`,
                id,
              )
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={groupIdValue || ""}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (!val || val === "") {
                nextFilters = removeConditions(filters, "groupId");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "groupId",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ກຸ່ມ"
            className="h-8 w-full sm:w-40"
            clearable
          />

          <InfiniteMultiCombobox<ClassTemplateLookupItem>
            queryKey={["classes", "templates", "lookup", "multi"]}
            queryFn={async (params: { search: string; pageParam: number }) => {
              const limit = 20;
              const skip = (params.pageParam - 1) * limit;
              const url = new URL(classTemplatesLookupUrl);
              url.searchParams.set("limit", String(limit));
              url.searchParams.set("skip", String(skip));
              if (params.search) url.searchParams.set("q", params.search);
              return fetchLookupForInfinite<ClassTemplateLookupItem>(
                url.toString(),
                params,
              );
            }}
            preloadQueryFn={(ids) =>
              hydrateLookupItems<ClassTemplateLookupItem>(
                classTemplatesLookupUrl,
                ids,
              )
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            values={classTemplateIds}
            onValuesChange={(vals) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (vals.length === 0) {
                nextFilters = removeConditions(filters, "classTemplateId");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "classTemplateId",
                  op: "in",
                  value: vals,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ຄລາດ (ເລືອກໄດ້ຫຼາຍ)"
            className="h-8 w-full sm:w-56"
            clearable
          />

          <SimpleSelect
            value={statusValue || "all"}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "status");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "status",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ສະຖານະ"
            className="h-8 w-full sm:w-32"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "active", label: "ໃຊ້ງານ" },
              { value: "deactivated", label: "ປິດການໃຊ້ງານ" },
            ]}
          />
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({ search: { ...search, filters: undefined, offset: 0 } })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
      {classTemplateIds.length > 0 ? (
        <p className="text-muted-foreground text-xs">
          ເລືອກຄລາດແລ້ວ {classTemplateIds.length} ລາຍການ
        </p>
      ) : null}
    </div>
  );
}
